/**
 * Mutation for moving an animal group via the atomic grazing-event endpoint
 * POST /v3/grazingEvents/move.
 *
 * The move is expressed as two explicit operations the backend applies in one
 * transaction: close the currently-open event (move-out date + post cover) and
 * open a new event (move-in date + pre cover) with a client-generated guid that
 * serves as the idempotency key. The endpoint has no NONE-reading side effects.
 *
 * Authorization and cross-farm failures come back as HTTP 409. When the source
 * field has no open event, the caller may send `close.field` to create a
 * move-out-only row instead of closing by guid.
 *
 * Cover values are passed in display units and divided by 10000 here to match
 * the stored-unit convention the reading API uses.
 */
import {
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { syncAnimalGroupNowFromMoveEvents } from "model/fieldCacheHelpers";
import type { Field } from "model/fieldTypes";
import { deleteAllTilesForField } from "services";
import type { MoveEvent } from "./useAnimalGroupQueries";

export const MOVE_ANIMAL_GROUP_MUTATION_KEY = ["moveAnimalGroup"];

/** TanStack mutation scope id — one in-flight move per animal group at a time. */
export function grazingMoveScopeId(animalGroupGuid: string): string {
  return `grazing-move:${animalGroupGuid}`;
}

export class GrazingMoveConflictError extends Error {
  constructor(public code: string) {
    super(code);
    this.name = "GrazingMoveConflictError";
  }
}

interface GrazingMovePayload {
  close?: {
    guid: string;
    field?: string | null;
    dateTimeOut: string;
    postEventCover?: number | null;
  } | null;
  open: {
    guid: string;
    field?: string | null;
    movedToShed: boolean;
    dateTimeIn: string;
    preEventCover?: number | null;
    animalGroupGuid?: string | null;
  };
}

async function postGrazingEventMove(
  payload: GrazingMovePayload
): Promise<{ guid: string; idempotent: boolean }> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v3/grazingEvents/move`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new GrazingMoveConflictError(body.error ?? "CONFLICT");
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw error;
  }
  return (await res.json()) as { guid: string; idempotent: boolean };
}

export interface MoveAnimalGroupVariables {
  animalGroupGuid: string;
  /**
   * Client-generated guid for the new (opened) event. It is the idempotency
   * key, so the caller must generate it once per logical move and reuse it on
   * a resubmit; a replay returns the same event without creating a duplicate.
   */
  openEventGuid: string;
  /**
   * Guid sent as `close.guid`: an existing open event to close, or a
   * client-generated idempotency key for a new move-out-only row (when
   * `closeSourceFieldGuid` is also set).
   */
  closeEventGuid?: string;
  /**
   * When set together with a client-generated `closeEventGuid`, the backend
   * creates a move-out-only row on this field instead of closing by guid.
   */
  closeSourceFieldGuid?: string;
  /** Source field, used for the optimistic clear. Omit for a move from shed. */
  sourceFieldGuid?: string;
  /** Destination field. Omit (with movedToShed) for a move to shed. */
  destFieldGuid?: string;
  movedToShed: boolean;
  moveDate: Date;
  /** Display-unit covers; divided by 10000 before sending. */
  preGrazingCover?: number;
  postGrazingCover?: number;
}

type MoveResult = { guid: string; idempotent: boolean };

interface MoveContext {
  snapshot: Array<[QueryKey, unknown]>;
}

function moveMutationOptions(queryClient: QueryClient) {
  return {
    mutationKey: MOVE_ANIMAL_GROUP_MUTATION_KEY,
    mutationFn: (vars: MoveAnimalGroupVariables): Promise<MoveResult> =>
      postGrazingEventMove({
        close: vars.closeEventGuid
          ? {
              guid: vars.closeEventGuid,
              field: vars.closeSourceFieldGuid ?? null,
              dateTimeOut: new Date(vars.moveDate).toISOString(),
              postEventCover:
                vars.postGrazingCover != null
                  ? vars.postGrazingCover / 10000
                  : null,
            }
          : null,
        open: {
          guid: vars.openEventGuid,
          field: vars.movedToShed ? null : vars.destFieldGuid ?? null,
          movedToShed: vars.movedToShed,
          dateTimeIn: new Date(vars.moveDate).toISOString(),
          preEventCover:
            vars.preGrazingCover != null ? vars.preGrazingCover / 10000 : null,
          animalGroupGuid: vars.animalGroupGuid,
        },
      }),
    onMutate: async (vars: MoveAnimalGroupVariables) => {
      const snapshot: Array<[QueryKey, unknown]> = [];

      const moveEventsKey = queryKeys.animalGroups.moveEvents(
        vars.animalGroupGuid
      );
      await queryClient.cancelQueries({ queryKey: moveEventsKey });
      const previousMoveEvents =
        queryClient.getQueryData<MoveEvent[]>(moveEventsKey) ?? [];
      snapshot.push([moveEventsKey, previousMoveEvents]);
      const dateIso = new Date(vars.moveDate).toISOString();
      const isOutOnlyClose = !!vars.closeSourceFieldGuid;
      let withClosed = previousMoveEvents.map((event) =>
        !isOutOnlyClose && event.guid === vars.closeEventGuid
          ? {
              ...event,
              dateTimeOut: dateIso,
              endDryMatter: vars.postGrazingCover ?? event.endDryMatter ?? null,
            }
          : event
      );
      if (isOutOnlyClose && vars.closeEventGuid && vars.closeSourceFieldGuid) {
        const sourceField = queryClient.getQueryData<Field>(
          queryKeys.fields.detail(vars.closeSourceFieldGuid)
        );
        withClosed = [
          ...withClosed,
          {
            guid: vars.closeEventGuid,
            moveNumber: withClosed.length + 1,
            fieldGuid: vars.closeSourceFieldGuid,
            fieldName: sourceField?.name ?? null,
            dateTimeIn: null,
            dateTimeOut: dateIso,
            startDryMatter: null,
            endDryMatter: vars.postGrazingCover ?? null,
            movedToShed: false,
            kind: "user" as const,
            verificationStatus: "Accepted" as const,
            animalGroupGuid: vars.animalGroupGuid,
          },
        ];
      }
      const destField = vars.destFieldGuid
        ? queryClient.getQueryData<Field>(
            queryKeys.fields.detail(vars.destFieldGuid)
          )
        : undefined;
      const newEvent: MoveEvent = {
        guid: vars.openEventGuid,
        moveNumber: withClosed.length + 1,
        fieldGuid: vars.movedToShed ? null : vars.destFieldGuid ?? null,
        fieldName: vars.movedToShed ? null : destField?.name ?? null,
        dateTimeIn: dateIso,
        dateTimeOut: null,
        startDryMatter: vars.preGrazingCover ?? null,
        endDryMatter: null,
        movedToShed: vars.movedToShed,
        kind: "user",
        verificationStatus: "Accepted",
        animalGroupGuid: vars.animalGroupGuid,
      };
      const updatedMoveEvents = [...withClosed, newEvent];
      queryClient.setQueryData<MoveEvent[]>(moveEventsKey, updatedMoveEvents);

      await queryClient.cancelQueries({ queryKey: queryKeys.fields.all });
      snapshot.push(
        ...syncAnimalGroupNowFromMoveEvents(
          queryClient,
          vars.animalGroupGuid,
          updatedMoveEvents
        )
      );

      return { snapshot };
    },
    onError: (
      _error: unknown,
      vars: MoveAnimalGroupVariables,
      context: MoveContext | undefined
    ) => {
      if (context?.snapshot) {
        context.snapshot.forEach(([key, previous]) => {
          queryClient.setQueryData(key, previous);
        });
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.animalGroups.moveEvents(vars.animalGroupGuid),
        });
      }
    },
    onSuccess: async (_data: MoveResult, vars: MoveAnimalGroupVariables) => {
      const fieldGuids = [vars.sourceFieldGuid, vars.destFieldGuid].filter(
        (g): g is string => !!g
      );
      await Promise.all(
        fieldGuids.map((fieldGuid) => deleteAllTilesForField(fieldGuid))
      );
    },
    onSettled: async (
      _data: MoveResult | undefined,
      _error: unknown,
      vars: MoveAnimalGroupVariables
    ) => {
      const fieldGuids = [vars.sourceFieldGuid, vars.destFieldGuid].filter(
        (g): g is string => !!g
      );
      await Promise.all(
        fieldGuids.flatMap((guid) => [
          queryClient.invalidateQueries({
            queryKey: queryKeys.fields.detail(guid),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.events.readings.byField(guid),
          }),
        ])
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.moveEvents(vars.animalGroupGuid),
      });
    },
  };
}

export function useMoveAnimalGroup() {
  const queryClient = useQueryClient();
  return useMutation(moveMutationOptions(queryClient));
}
